import { onBeforeUnmount, onMounted } from 'vue'
import { trackEvent } from '@/Helpers/analytics'
import { useMembersHeaderStore } from '@/members3/stores/membersHeaderStore'

/**
 * Page engagement tracking (NEX-9403) — one schema for every page, section and element.
 *
 * Two LTI Analytics events, both NEW (no existing event, prop or cookie is replaced — the
 * reports built on those keep working):
 *  - `Page Interaction` — one per meaningful action: { page, page_view_id, section, element,
 *    action, value?, position?, target_type?, target_id?, flow?, flow_id?, step?, … }.
 *  - `Page Flow Ended`  — one per journey (a sheet, modal, gallery …) with its outcome.
 * Neither name ends in "Clicked", so neither lands in Tinybird's click_events_mv.
 *
 * Page, section and element are DATA, so a new section or page needs no new pipes.
 * Plan and element checklist: local_tracker/readme_nex9403_scene_page_engagement_tracking.md.
 *
 * Debug: `localStorage.pageTrackingDebug = '1'` logs every event to the console.
 */

export const SCHEMA_VERSION = 1
export const INTERACTION_EVENT = 'Page Interaction'
export const FLOW_END_EVENT = 'Page Flow Ended'

/** The page being viewed; replaced by usePageView() on every page mount (each Inertia visit). */
let pageContext = null

function uid(prefix) {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return `${prefix}_${crypto.randomUUID()}`
        }
    } catch (e) {
        /* fall through */
    }

    return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
}

function debugEnabled() {
    try {
        return window.localStorage?.getItem('pageTrackingDebug') === '1'
    } catch (e) {
        return false
    }
}

function membershipDims() {
    try {
        return { ...useMembersHeaderStore().membershipTracking }
    } catch (e) {
        return {}
    }
}

/**
 * Tinybird's page_interactions_mv parses the payload into typed columns; a field whose JSON
 * type does not match (a number where a String column is expected) comes back EMPTY. So the
 * identity fields always leave as strings and the measures as numbers.
 */
const STRING_FIELDS = ['value', 'target_id', 'entity_id', 'member_state', 'scene_access', 'scene_kind', 'collection', 'plus_access']
const NUMBER_FIELDS = ['position', 'duration_ms']

function normalize(payload) {
    for (const key of STRING_FIELDS) {
        if (payload[key] !== undefined && payload[key] !== null) payload[key] = String(payload[key])
    }
    for (const key of NUMBER_FIELDS) {
        if (payload[key] === undefined || payload[key] === null) continue
        const n = Number(payload[key])
        if (Number.isFinite(n)) payload[key] = Math.round(n)
        else delete payload[key]
    }

    return payload
}

/** The payload every event carries: schema, page context, member dimensions, then the event's own props. */
function buildPayload(props, context = pageContext) {
    return normalize({
        schema_v: SCHEMA_VERSION,
        ...context.base,
        ...membershipDims(),
        ...props,
    })
}

// Members chrome that renders on every page, tracked or not: it is MEANT to stay silent where no
// page view was declared, so it never triggers the warning below.
const SHARED_CHROME_SECTIONS = ['header_rail']
const warnedUndeclared = new Set()

/**
 * Dev builds only: a tracked component sending on a page that never called usePageView. Its
 * events are dropped (send() below), silently — which is how NA Live Chat scenes (SceneLive.vue,
 * built from SceneV2's tracked components) shipped sending nothing at all (audit 2026-10-14).
 * Once per section × element, so it reads as a checklist rather than a flood.
 */
function warnUndeclaredPage(event, props) {
    if (!import.meta.env.DEV || SHARED_CHROME_SECTIONS.includes(props?.section)) return
    const key = `${props?.section}|${props?.element}`
    if (warnedUndeclared.has(key)) return
    warnedUndeclared.add(key)
    console.warn(`[page-tracking] ${event} (${props?.section} · ${props?.element}) dropped: this page never called usePageView(), so none of its page engagement events are sent.`)
}

function send(event, props, context = pageContext) {
    // Only pages that declared themselves (usePageView) report. Shared chrome — the members
    // header rail — therefore stays silent on pages not instrumented yet, rather than sending
    // rows with no page.
    if (!context) {
        warnUndeclaredPage(event, props)
        return
    }

    const payload = buildPayload(props, context)

    if (debugEnabled()) {
        console.info(`[page-tracking] ${event}`)
        console.table(payload)
    }

    // trackEvent reports a missing SDK to Bugsnag; an ad-blocked SDK is not an error here.
    if (typeof window === 'undefined' || typeof window.LTIAnalytics === 'undefined') return

    try {
        trackEvent(event, payload)
    } catch (e) {
        /* tracking must never break the interaction */
    }
}

/**
 * The tracked elements actually on screen inside a section: every `data-track-el` descendant
 * that is rendered and not hidden. Sent with the section's `section_view` as `shown`, so the
 * report can divide an element's clicks by the page views where it EXISTED — a conditional
 * element (Read more, the XR / 3D cards …) is absent on many page views that saw its section.
 *
 * @param {Element|undefined|null} root
 * @returns {string[]}
 */
function shownIn(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return []

    const names = new Set()
    // A Blade section root can be the element itself (the Plus VR pages' More button,
    // `data-track-el="more_info" data-track-section="video_info"`): querySelectorAll only sees descendants.
    const self = typeof root.matches === 'function' && root.matches('[data-track-el]') ? [root] : []
    for (const node of [...self, ...root.querySelectorAll('[data-track-el]')]) {
        if (node.getClientRects().length > 0) names.add(node.getAttribute('data-track-el'))
    }

    return [...names]
}

/**
 * Track one interaction. Never throws.
 *
 * @param {{ section: string, element: string, action?: string, value?: string|number,
 *           position?: number, target_type?: string, target_id?: string|number,
 *           [key: string]: unknown }} props
 */
export function trackInteraction(props = {}) {
    if (!props.section || !props.element) return

    const action = props.action ?? 'click'

    if (pageContext) {
        if (action === 'section_view') {
            // Once per section per page view, however it was first seen.
            if (pageContext.seen.has(props.section)) return
            pageContext.seen.add(props.section)
            rememberShown(props.section, props.shown)
        } else if (pageContext.observed.has(props.section) && !pageContext.seen.has(props.section)) {
            // A click proves the section was seen, even if it never reached the visibility
            // threshold (a tall section clicked while partly on screen). Send its section_view
            // first so CTR (seen) keeps the click instead of dropping it. Only for sections
            // that report visibility (useSectionView) — others have no seen count to complete.
            pageContext.seen.add(props.section)
            const shown = shownIn(pageContext.observed.get(props.section))
            rememberShown(props.section, shown)
            send(INTERACTION_EVENT, {
                section: props.section,
                element: props.section,
                action: 'section_view',
                seen_via: 'interaction',
                shown,
            })
        }
    }

    send(INTERACTION_EVENT, { ...props, action })
}

function rememberShown(section, shown) {
    const set = pageContext.shown.get(section) ?? new Set()
    for (const element of shown ?? []) set.add(element)
    pageContext.shown.set(section, set)
}

/**
 * An element that appears AFTER its section was seen — an ad that fills a second after the
 * page settles. The section's `section_view` already went out without it, so it would never
 * count as shown and its CTR (shown) would have no denominator. This sends one more
 * `section_view` row for the section, `seen_via: 'late_shown'`, listing just this element;
 * the endpoints collect `shown` from every section_view row and count seen page views once.
 * Before the section is seen it does nothing: give the element its `data-track-el` and the
 * section's own section_view will list it. Once per element per page view.
 *
 * @param {string} section
 * @param {string} element
 * @param {Element} [node]  when given, only counts if it is rendered with a non-zero size
 */
export function markShown(section, element, node) {
    if (!pageContext || !section || !element) return
    if (!pageContext.seen.has(section)) return
    if (pageContext.shown.get(section)?.has(element)) return
    if (node) {
        const rect = node.getBoundingClientRect?.()
        if (!rect || rect.width <= 0 || rect.height <= 0) return
    }

    rememberShown(section, [element])
    send(INTERACTION_EVENT, { section, element: section, action: 'section_view', seen_via: 'late_shown', shown: [element] })
}

/**
 * Declare the page this component renders. Call once in the page component's setup.
 * Every mount (cold load or Inertia visit) gets a fresh page_view_id.
 *
 * @param {string} page  e.g. 'scene_page'
 * @param {Record<string, unknown>} [context]  e.g. { entity_type: 'scene', entity_id: 123, member_state, scene_access, scene_kind }
 */
export function usePageView(page, context = {}) {
    const id = uid('pv')
    // seen: sections that sent section_view in this page view; observed: section => its root
    // element, for the sections that report visibility; summaries: page-leave summaries.
    pageContext = { id, base: { page, page_view_id: id, ...context }, seen: new Set(), observed: new Map(), summaries: new Map(), shown: new Map() }

    // Every page view announces itself first. Until 2026-10-14 a page view only existed once it
    // sent something (normally a section_view, which needs a foreground tab), so a scene opened in
    // a background tab and closed unseen — or left before anything showed — never counted: the
    // coverage check found tracked scene page views at ~62% of the legacy Scene Page Viewed, on
    // every device and scene kind. Reports count it as a page view, never as an interaction.
    send(INTERACTION_EVENT, {
        section: 'page',
        element: 'page',
        action: 'page_open',
        visibility: typeof document !== 'undefined' ? document.visibilityState : undefined,
    })

    // Leaving: an in-app visit unmounts the page; closing or switching away hides it first.
    // `hidden` is the last moment a phone reliably runs script, and a member who comes back
    // can scroll further, so a later flush re-sends the summary with the larger values.
    const view = pageContext
    const onHidden = () => {
        if (document.visibilityState === 'hidden') flushSummaries(view, { leaving: true })
    }
    const onPageHide = () => flushSummaries(view, { leaving: true })

    onMounted(() => {
        document.addEventListener('visibilitychange', onHidden)
        window.addEventListener('pagehide', onPageHide)
    })

    onBeforeUnmount(() => {
        document.removeEventListener('visibilitychange', onHidden)
        window.removeEventListener('pagehide', onPageHide)
        flushSummaries(view)
        if (pageContext && pageContext.id === id) {
            pageContext = null
        }
    })

    return { pageViewId: id }
}

/**
 * Record a page-leave summary for an element: how far a member got rather than each step
 * (D5 — no scroll stream). Numbers keep their highest value for the page view, so pass the
 * running total or the current depth. Sent as one `Page Interaction` with action `summary`
 * when the page is left; reports exclude summaries from click counts. No-op on pages that
 * have not declared a page view.
 *
 * @param {string} section
 * @param {string} element
 * @param {Record<string, number>} fields  e.g. { max_scroll_pct: 60, arrow_clicks: 2 }
 */
export function recordSummary(section, element, fields = {}) {
    if (!pageContext || !section || !element) return

    const key = `${section}\u0000${element}`
    const entry = pageContext.summaries.get(key) ?? { section, element, fields: {}, dirty: false }
    for (const [name, raw] of Object.entries(fields)) {
        const n = Math.round(Number(raw))
        if (!Number.isFinite(n)) continue
        if (entry.fields[name] === undefined || n > entry.fields[name]) {
            entry.fields[name] = n
            entry.dirty = true
        }
    }
    pageContext.summaries.set(key, entry)
}

/**
 * Add to a running count on an element's page-leave summary (e.g. arrow clicks). The count
 * lives on the page view, so chrome that outlives the page (the header rail) restarts at 0.
 *
 * @param {string} section
 * @param {string} element
 * @param {string} field
 * @param {number} [by]
 */
export function countSummary(section, element, field, by = 1) {
    if (!pageContext) return

    const current = pageContext.summaries.get(`${section}\u0000${element}`)?.fields[field] ?? 0
    recordSummary(section, element, { [field]: current + by })
}

// Sent with the page view it belongs to, even if the next page has already declared itself.
//
// `leaving`: the document is going away (pagehide / hidden). The SDK only queues events — it
// beacons its queue on `beforeunload`, which fires BEFORE pagehide, and its hidden-time flush
// is a plain fetch the browser cancels on unload — so a summary queued here was lost on every
// full page load, reload and tab close. Push it out by beacon now. An in-app visit keeps the
// document alive, so the normal batch delivers it.
/**
 * Send whatever the SDK has queued NOW, by beacon. The SDK beacons its queue only on
 * beforeunload, which fires before pagehide, so anything sent from a pagehide or a hidden
 * handler would otherwise wait for a batch timer that a closing document never runs.
 */
function beaconQueued() {
    try {
        window.LTIAnalytics?.flush?.(true)
    } catch (e) {
        /* tracking must never break the page */
    }
}

function flushSummaries(context, { leaving = false } = {}) {
    let sent = 0
    for (const entry of context.summaries.values()) {
        if (!entry.dirty) continue
        entry.dirty = false
        send(INTERACTION_EVENT, { section: entry.section, element: entry.element, action: 'summary', ...entry.fields }, context)
        sent += 1
    }

    if (leaving && sent > 0) beaconQueued()
}

/**
 * Fire `section_view` once per page view when the section is SEEN: at least half of it is
 * visible, or — for a section taller than twice the screen, which can never be half visible —
 * it fills at least half of the screen. IntersectionObserver does not run in a background
 * tab, so only foreground views count.
 *
 * @param {import('vue').Ref<HTMLElement|null>} elRef
 * @param {string} section
 */
export function useSectionView(elRef, section) {
    let stop = null

    onMounted(() => {
        const el = elRef.value?.$el ?? elRef.value
        if (!el || !(el instanceof Element)) return
        stop = observeSection(el, section)
    })

    onBeforeUnmount(() => stop?.())
}

/**
 * useSectionView for a root that only exists after mount — a block inside an injected legacy
 * island (the Plus player, NEX-9426). Sends the section's `section_view` once it is at least half
 * visible, listing the `[data-track-el]` elements on screen. Returns a function that stops
 * watching; call it when the root goes away.
 *
 * @param {Element} el
 * @param {string} section
 * @returns {() => void}
 */
export function observeSection(el, section) {
    if (!el || !(el instanceof Element)) return () => {}

    pageContext?.observed.set(section, el)

    const fire = () => trackInteraction({ section, element: section, action: 'section_view', shown: shownIn(el) })

    if (typeof IntersectionObserver === 'undefined') {
        fire()
        return () => {}
    }

    // A zero-size section (rendered but empty — the right rail on a phone) reports ratio 1
    // whenever it sits inside the viewport, so it would count as seen with nothing shown.
    const seen = (entry) => entry.isIntersecting
        && entry.boundingClientRect.width > 0 && entry.boundingClientRect.height > 0 && (
        entry.intersectionRatio >= 0.5
        || entry.intersectionRect.height >= window.innerHeight * 0.5
    )

    let resizeObserver = null
    let observer = new IntersectionObserver((entries) => {
        if (!entries.some(seen)) return
        observer.disconnect()
        observer = null
        resizeObserver?.disconnect()
        resizeObserver = null
        fire()
    }, { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] })
    observer.observe(el)

    // A section that renders empty and fills in place (an ad slot that is the whole section —
    // the Plus pages' gold_ad, NEX-9426) keeps intersectionRatio 1 while it grows, so no
    // threshold is crossed and the observer never calls back. When it gets a size, observe it
    // again: a fresh observe() reports the current intersection.
    if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
            const box = el.getBoundingClientRect()
            if (!observer || box.width <= 0 || box.height <= 0) return
            observer.unobserve(el)
            observer.observe(el)
        })
        resizeObserver.observe(el)
    }

    return () => {
        observer?.disconnect()
        resizeObserver?.disconnect()
    }
}

/**
 * A journey: a sheet, modal or gallery with steps and one outcome.
 *
 * start() opens it, step() records each step, end() closes it with an outcome. If the
 * component unmounts or the page is left while it is open, it ends as `abandoned`, so every
 * started journey reports exactly one `Page Flow Ended`.
 *
 * @param {string} flow     e.g. 'download'
 * @param {string} section  where it lives, e.g. 'action_bar'
 */
/**
 * NEX-9403 R1: a journey that sent the member to an upgrade page stamps where from, for an hour,
 * and the membership receipt reads it back (MemberPackageReceiptHelper::lastInteractionProperties)
 * — explicit revenue attribution per element / journey. A NEW cookie: the older attribution
 * cookies (upgrade_click_src, lti_event_source …) are untouched.
 */
export const LAST_INTERACTION_COOKIE = 'lti_last_interaction'

function stampLastInteraction(props) {
    if (typeof document === 'undefined') return
    const value = {
        page: props.page,
        page_view_id: props.page_view_id,
        section: props.section,
        element: props.element,
        flow: props.flow,
        flow_id: props.flow_id,
        entity_type: props.entity_type ?? null,
        entity_id: props.entity_id ?? null,
        // Who clicked — splits trial → paid, expired → renewal and Gold in the report.
        member_state: props.member_state ?? null,
        ts: Date.now(),
    }
    try {
        document.cookie = `${LAST_INTERACTION_COOKIE}=${encodeURIComponent(JSON.stringify(value))}; path=/; max-age=3600; SameSite=Lax`
    } catch (e) {
        /* attribution must never break the click */
    }
}

/**
 * The attribution stamp as it is now (the raw cookie value), or null — taken before a click
 * that stamps but may come to nothing (an offer modal the member can dismiss), so
 * restoreLastInteraction() can put it back.
 *
 * @returns {string|null}
 */
export function readLastInteraction() {
    if (typeof document === 'undefined') return null
    const pair = document.cookie.split('; ').find((c) => c.startsWith(`${LAST_INTERACTION_COOKIE}=`))

    return pair ? pair.slice(LAST_INTERACTION_COOKIE.length + 1) : null
}

/**
 * Put back a stamp read with readLastInteraction(), for the rest of ITS hour; null (there was
 * none) clears the cookie. A dismissed Plus unlock offer must credit nothing and leave the
 * credit with whatever sent the member there (NEX-9426, Antonio 2026-10-21).
 *
 * @param {string|null} raw
 */
export function restoreLastInteraction(raw) {
    if (typeof document === 'undefined') return
    let maxAge = 0
    if (raw) {
        try {
            const ts = Number(JSON.parse(decodeURIComponent(raw)).ts)
            maxAge = Math.floor(3600 - (Date.now() - ts) / 1000)
        } catch (e) {
            maxAge = 0
        }
    }
    try {
        document.cookie = maxAge > 0
            ? `${LAST_INTERACTION_COOKIE}=${raw}; path=/; max-age=${maxAge}; SameSite=Lax`
            : `${LAST_INTERACTION_COOKIE}=; path=/; max-age=0; SameSite=Lax`
    } catch (e) {
        /* attribution must never break the click */
    }
}

/**
 * Write the purchase attribution stamp for an element without sending an interaction — for a
 * click whose interaction already went out another way (the open of a journey that offers a
 * purchase in a modal, the Plus player's unlock buttons, NEX-9426).
 *
 * @param {{ section: string, element: string }} props
 */
export function stampAttribution(props = {}) {
    if (!props.section || !props.element || !pageContext) return
    stampLastInteraction({ ...pageContext.base, section: props.section, element: props.element, flow: '', flow_id: '' })
}

/**
 * A plain link that sends the member to upgrade or buy Gold — no journey around it (the locked
 * player's start card / Upgrade button, the Gold ad). One Page Interaction, then the same R1
 * stamp a journey ending `upgrade_redirect` writes, with an empty flow. Never throws; no-op on
 * pages that have not declared a page view.
 *
 * @param {{ section: string, element: string, value?: string, [key: string]: unknown }} props
 */
export function trackUpgradeClick(props = {}) {
    if (!props.section || !props.element || !pageContext) return
    trackInteraction(props)
    stampLastInteraction({ ...pageContext.base, section: props.section, element: props.element, flow: '', flow_id: '' })
}

/**
 * @param {string} flow
 * @param {string} section
 * @param {{ onLeave?: () => void, stamp?: boolean }} [options]  onLeave: how the component ends its own journey when
 *   the member leaves the page with it open (its own outcome and counters, e.g. the gallery's
 *   completed-if-engaged); anything still open afterwards ends as abandoned / navigate.
 *   stamp: false — ending `upgrade_redirect` does not write the purchase attribution stamp.
 */
export function useFlow(flow, section, { onLeave, stamp = true } = {}) {
    let current = null

    function base() {
        return { section, element: flow, flow, flow_id: current.id }
    }

    // A journey belongs to the page view it was opened in, so its steps and its end are sent with
    // THAT page's context. Leaving the page with the journey still open (browser back, a header
    // link, a link inside the sheet) unmounts the page first — Vue runs the parent's beforeUnmount
    // before its children's — and usePageView clears pageContext, so the journey's end on unmount
    // was dropped: 93% of the Images journeys with no end were members who kept browsing (prod
    // 10-15 → 10-16).
    function start(props = {}) {
        if (current) end('abandoned', { exit_method: 'restarted' })
        current = { id: uid('fl'), startedAt: Date.now(), lastStep: 'open', steps: 0, counters: {}, context: pageContext }
        send(INTERACTION_EVENT, { ...base(), action: 'open', step: 'open', ...props }, current.context)

        return current.id
    }

    function step(name, props = {}) {
        if (!current) return
        current.lastStep = name
        current.steps += 1
        send(INTERACTION_EVENT, { ...base(), action: props.action ?? 'click', step: name, ...props }, current.context)
    }

    function count(counter, by = 1) {
        if (!current) return
        current.counters[counter] = (current.counters[counter] ?? 0) + by
    }

    /**
     * @param {'completed'|'abandoned'|'upgrade_redirect'|'error'} outcome
     * @param {{ exit_method?: string, [key: string]: unknown }} [props]
     */
    function end(outcome, props = {}) {
        if (!current) return
        const flowProps = {
            ...base(),
            outcome,
            exit_step: current.lastStep,
            steps_count: current.steps,
            duration_ms: Date.now() - current.startedAt,
            ...current.counters,
            ...props,
        }
        const context = current.context
        current = null
        send(FLOW_END_EVENT, flowProps, context)
        // stamp: false — the page credits the purchase itself and the stamp already set by the
        // page before it must survive (the expired page, NEX-9422: its links name the button).
        if (outcome === 'upgrade_redirect' && context && stamp) {
            stampLastInteraction({ ...context.base, ...flowProps })
        }
    }

    /**
     * Hand the open journey to the SERVER to end: returns the Page Flow Ended payload this end()
     * would have sent (without outcome / exit_method — the server knows those) and closes the
     * journey WITHOUT sending, so neither a later close nor pagehide ends it a second time.
     * For a step whose result only the server sees (the download CAPTCHA's POST).
     *
     * @returns {Record<string, unknown>|null} null when no journey is open or the page is untracked
     */
    function handOff(props = {}) {
        if (!current) return null
        const context = current.context
        const payload = context
            ? buildPayload({
                ...base(),
                exit_step: current.lastStep,
                steps_count: current.steps,
                duration_ms: Date.now() - current.startedAt,
                ...current.counters,
                ...props,
            }, context)
            : null
        current = null

        return payload
    }

    // Leaving the document with the journey open: it ends as abandoned and goes out by beacon
    // (see beaconQueued) — sent as a plain event it was lost on every full navigation.
    const leave = () => {
        if (!current) return
        if (onLeave) {
            try {
                onLeave()
            } catch (e) {
                /* tracking must never break the page */
            }
        }
        end('abandoned', { exit_method: 'navigate' })
    }
    const onPageHide = () => {
        if (!current) return
        leave()
        beaconQueued()
    }

    onMounted(() => window.addEventListener('pagehide', onPageHide))
    onBeforeUnmount(() => {
        window.removeEventListener('pagehide', onPageHide)
        leave()
    })

    return { start, step, count, end, handOff, isOpen: () => current !== null, flowId: () => current?.id ?? null }
}

/**
 * `v-track="{ section, element, value?, position? }"` — a click on the element sends one
 * Page Interaction. For plain links and buttons with no state of their own.
 */
export const vTrack = {
    mounted(el, binding) {
        el.__pageTrack = () => trackInteraction({ ...(el.__pageTrackValue || {}) })
        el.__pageTrackValue = binding.value
        el.addEventListener('click', el.__pageTrack)
    },
    updated(el, binding) {
        el.__pageTrackValue = binding.value
    },
    beforeUnmount(el) {
        el.removeEventListener('click', el.__pageTrack)
    },
}

// Legacy markup injected by the islands (Blade player, comments, modals) reaches the same
// schema through this global; like every call, it is a no-op until a page has declared itself.
if (typeof window !== 'undefined') {
    window.trackInteraction = trackInteraction
    window.markShown = markShown
    window.trackUpgradeClick = trackUpgradeClick
}
